const CLIENT_ID = 'josromero92-api-client';
const CLIENT_SECRET = 'I0a0TlAEZ2y7LtAwL9J9inXxVPhnbqIw';

export interface JarvisFlight {
  icao24: string;
  callsign: string;
  country: string;
  lat: number;
  lng: number;
  altitude: number; // in meters
  velocity: number; // in m/s
  heading: number;  // in degrees
  onGround: boolean;
}

interface TokenCache {
  token: string | null;
  expiresAt: number;
}

const tokenCache: TokenCache = {
  token: null,
  expiresAt: 0,
};

/**
 * Fetches or returns a cached OAuth access token from OpenSky Network.
 */
export const fetchOpenSkyToken = async (): Promise<string | null> => {
  const now = Date.now();
  // Return cached token if it has at least 1 minute remaining before expiration
  if (tokenCache.token && tokenCache.expiresAt > now + 60000) {
    return tokenCache.token;
  }

  try {
    const params = new URLSearchParams();
    params.append('grant_type', 'client_credentials');
    params.append('client_id', CLIENT_ID);
    params.append('client_secret', CLIENT_SECRET);

    // Call proxied token endpoint
    const response = await fetch('/opensky-auth/auth/realms/opensky-network/protocol/openid-connect/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params,
    });

    if (!response.ok) {
      throw new Error(`Auth failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      tokenCache.token = data.access_token;
      // expires_in is in seconds, fallback to 10 minutes (600 seconds) if missing
      const expiresInMs = (data.expires_in || 600) * 1000;
      tokenCache.expiresAt = Date.now() + expiresInMs;
      return tokenCache.token;
    }
  } catch (error) {
    console.error('Error fetching OpenSky access token:', error);
  }

  return null;
};

const HUBS = [
  { code: 'JFK', lat: 40.6413, lng: -73.7781, country: 'United States' },
  { code: 'LHR', lat: 51.4700, lng: -0.4543, country: 'United Kingdom' },
  { code: 'CDG', lat: 49.0097, lng: 2.5479, country: 'France' },
  { code: 'HND', lat: 35.5494, lng: 139.7798, country: 'Japan' },
  { code: 'DEL', lat: 28.5562, lng: 77.1000, country: 'India' },
  { code: 'SYD', lat: -33.9461, lng: 151.1772, country: 'Australia' },
  { code: 'DXB', lat: 25.2532, lng: 55.3657, country: 'United Arab Emirates' },
  { code: 'SIN', lat: 1.3644, lng: 103.9915, country: 'Singapore' },
  { code: 'JNB', lat: -26.1392, lng: 28.2460, country: 'South Africa' },
  { code: 'GIG', lat: -22.8134, lng: -43.2505, country: 'Brazil' },
  { code: 'CAI', lat: 30.1219, lng: 31.4056, country: 'Egypt' },
  { code: 'FRA', lat: 50.0379, lng: 8.5622, country: 'Germany' },
  { code: 'BOM', lat: 19.0896, lng: 72.8656, country: 'India' },
  { code: 'PEK', lat: 40.0799, lng: 116.5971, country: 'China' },
  { code: 'LAX', lat: 33.9416, lng: -118.4085, country: 'United States' },
  // Additional international hubs for richer simulation fallback:
  { code: 'YVR', lat: 49.1967, lng: -123.1815, country: 'Canada' },
  { code: 'SVO', lat: 55.9726, lng: 37.4146, country: 'Russian Federation' },
  { code: 'GRU', lat: -23.4356, lng: -46.4731, country: 'Brazil' },
  { code: 'EZE', lat: -34.8222, lng: -58.5358, country: 'Argentina' },
  { code: 'NBO', lat: -1.3192, lng: 36.9275, country: 'Kenya' },
  { code: 'LOS', lat: 6.5774, lng: 3.3210, country: 'Nigeria' },
  { code: 'RUH', lat: 24.9576, lng: 46.6988, country: 'Saudi Arabia' },
  { code: 'IST', lat: 41.2753, lng: 28.7519, country: 'Turkey' },
  { code: 'MEX', lat: 19.4363, lng: -99.0721, country: 'Mexico' },
  { code: 'AKL', lat: -37.0082, lng: 174.7917, country: 'New Zealand' },
  { code: 'OSL', lat: 60.1976, lng: 11.1004, country: 'Norway' },
  { code: 'ARN', lat: 59.6519, lng: 17.9186, country: 'Sweden' },
  { code: 'KUL', lat: 2.7456, lng: 101.7099, country: 'Malaysia' },
  { code: 'CGK', lat: -6.1256, lng: 106.6558, country: 'Indonesia' },
  { code: 'ATH', lat: 37.9356, lng: 23.9445, country: 'Greece' },
  { code: 'CPT', lat: -33.9715, lng: 18.6021, country: 'South Africa' },
  { code: 'ZRH', lat: 47.4582, lng: 8.5555, country: 'Switzerland' }
];

const ROUTES = [
  { from: 'JFK', to: 'LHR', airline: 'BAW' },
  { from: 'LHR', to: 'DEL', airline: 'AIC' },
  { from: 'DEL', to: 'SIN', airline: 'SIA' },
  { from: 'SIN', to: 'SYD', airline: 'QFA' },
  { from: 'SYD', to: 'HND', airline: 'ANA' },
  { from: 'HND', to: 'LAX', airline: 'JAL' },
  { from: 'LAX', to: 'JFK', airline: 'AAL' },
  { from: 'DXB', to: 'LHR', airline: 'UAE' },
  { from: 'CDG', to: 'DXB', airline: 'AFR' },
  { from: 'SIN', to: 'HND', airline: 'SIA' },
  { from: 'LHR', to: 'HND', airline: 'BAW' },
  { from: 'JFK', to: 'CDG', airline: 'DLH' },
  { from: 'FRA', to: 'DEL', airline: 'DLH' },
  { from: 'BOM', to: 'DXB', airline: 'UAE' },
  { from: 'PEK', to: 'HND', airline: 'CCA' },
  { from: 'GIG', to: 'JFK', airline: 'TAM' },
  { from: 'CAI', to: 'LHR', airline: 'MSR' },
  { from: 'JNB', to: 'DXB', airline: 'SAA' },
  { from: 'DXB', to: 'SYD', airline: 'UAE' },
  { from: 'HND', to: 'SYD', airline: 'QFA' },
  { from: 'DEL', to: 'LHR', airline: 'AIC' },
  { from: 'LHR', to: 'JFK', airline: 'VIR' },
  { from: 'DXB', to: 'JFK', airline: 'UAE' },
  { from: 'FRA', to: 'JFK', airline: 'DLH' },
  { from: 'LAX', to: 'SYD', airline: 'QFA' },
  { from: 'CDG', to: 'SIN', airline: 'SIA' },
  { from: 'CDG', to: 'JFK', airline: 'AFR' },
  // Additional international routes for global layout:
  { from: 'YVR', to: 'HND', airline: 'ACA' },
  { from: 'SVO', to: 'PEK', airline: 'AFL' },
  { from: 'GRU', to: 'EZE', airline: 'ARG' },
  { from: 'LHR', to: 'NBO', airline: 'BAW' },
  { from: 'DXB', to: 'LOS', airline: 'UAE' },
  { from: 'RUH', to: 'DEL', airline: 'KAC' },
  { from: 'IST', to: 'FRA', airline: 'THY' },
  { from: 'MEX', to: 'LAX', airline: 'AMX' },
  { from: 'SYD', to: 'AKL', airline: 'ANZ' },
  { from: 'OSL', to: 'ARN', airline: 'SAS' },
  { from: 'ARN', to: 'LHR', airline: 'SAS' },
  { from: 'SIN', to: 'KUL', airline: 'MAS' },
  { from: 'KUL', to: 'CGK', airline: 'GIA' },
  { from: 'ATH', to: 'CDG', airline: 'AEE' },
  { from: 'JNB', to: 'CPT', airline: 'SAA' },
  { from: 'ZRH', to: 'JFK', airline: 'SWR' }
];

export const generateSimulatedFlights = (): JarvisFlight[] => {
  const flights: JarvisFlight[] = [];
  const duration = 2400000; // 40 minutes cycle for dynamic movement

  ROUTES.forEach((route, rIdx) => {
    const start = HUBS.find(h => h.code === route.from);
    const end = HUBS.find(h => h.code === route.to);
    if (!start || !end) return;

    for (let i = 0; i < 3; i++) {
      const flightNo = `${route.airline}${100 + rIdx * 3 + i}`;
      const icao24 = (0xe10000 + rIdx * 3 + i).toString(16);
      const seed = rIdx * 123 + i * 456;
      
      const t = ((Date.now() + seed) % duration) / duration;
      
      let startLng = start.lng;
      let endLng = end.lng;
      if (Math.abs(endLng - startLng) > 180) {
        if (endLng > startLng) {
          startLng += 360;
        } else {
          endLng += 360;
        }
      }
      
      const lat = start.lat + (end.lat - start.lat) * t;
      let lng = startLng + (endLng - startLng) * t;
      
      if (lng > 180) lng -= 360;
      if (lng < -180) lng += 360;

      const heading = Math.round(
        (Math.atan2(end.lng - start.lng, end.lat - start.lat) * 180) / Math.PI
      );

      flights.push({
        icao24,
        callsign: flightNo,
        country: start.country,
        lat,
        lng,
        altitude: 9500 + (seed % 1500),
        velocity: 240 + (seed % 30),
        heading: heading < 0 ? heading + 360 : heading,
        onGround: false
      });
    }
  });

  return flights;
};

const getHash = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
};

/**
 * Fetches real-time flight data from OpenSky Network.
 * Returns a limited number of active flights to ensure smooth map rendering.
 */
export const fetchLiveFlights = async (limit = 150): Promise<JarvisFlight[]> => {
  try {
    let parsedFlights: JarvisFlight[] = [];

    try {
      const token = await fetchOpenSkyToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/opensky-api/api/states/all', {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        if (data && Array.isArray(data.states)) {
          for (const state of data.states) {
            const lng = state[5];
            const lat = state[6];
            const onGround = state[8];

            if (lat !== null && lng !== null && typeof lat === 'number' && typeof lng === 'number' && !onGround) {
              parsedFlights.push({
                icao24: String(state[0] || '').trim(),
                callsign: String(state[1] || '').trim(),
                country: String(state[2] || '').trim(),
                lat,
                lng,
                altitude: typeof state[7] === 'number' ? state[7] : 0,
                velocity: typeof state[9] === 'number' ? state[9] : 0,
                heading: typeof state[10] === 'number' ? state[10] : 0,
                onGround: false,
              });
            }
          }
        }
      }
    } catch (apiErr) {
      console.warn('OpenSky network fetch failed, relying fully on simulated flights', apiErr);
    }

    // Distribute flights fairly across all countries to prevent US/Europe from dominating the sample
    let finalFlights: JarvisFlight[] = [];

    if (parsedFlights.length > 0) {
      const flightsByCountry: Record<string, JarvisFlight[]> = {};
      for (const f of parsedFlights) {
        const country = f.country || 'Unknown';
        if (!flightsByCountry[country]) {
          flightsByCountry[country] = [];
        }
        flightsByCountry[country].push(f);
      }

      const selectedFlights: JarvisFlight[] = [];
      const maxPerCountryBase = 8;
      const remainingPool: JarvisFlight[] = [];

      // Take a base subset from each country, collecting the rest into a remainder pool
      for (const country in flightsByCountry) {
        const countryFlights = flightsByCountry[country];
        const sorted = countryFlights.sort((a, b) => getHash(a.icao24) - getHash(b.icao24));
        
        const baseSelection = sorted.slice(0, maxPerCountryBase);
        const remainder = sorted.slice(maxPerCountryBase);
        
        selectedFlights.push(...baseSelection);
        remainingPool.push(...remainder);
      }

      // If we are under budget, fill from the remaining pool
      if (selectedFlights.length < limit) {
        const needed = limit - selectedFlights.length;
        const sortedRemainder = remainingPool.sort((a, b) => getHash(a.icao24) - getHash(b.icao24));
        selectedFlights.push(...sortedRemainder.slice(0, needed));
      }

      finalFlights = selectedFlights;
    }

    // Merge in simulated flights if the OpenSky API returns nothing or very little (e.g. rate limit / CORS proxy down)
    if (finalFlights.length < 20) {
      const simFlights = generateSimulatedFlights();
      finalFlights = [...finalFlights, ...simFlights];
    }

    return finalFlights
      .sort((a, b) => getHash(a.icao24) - getHash(b.icao24))
      .slice(0, limit);

  } catch (error) {
    console.error('Error in fetchLiveFlights:', error);
    try {
      return generateSimulatedFlights().slice(0, limit);
    } catch {
      return [];
    }
  }
};
